import type { ApolloError } from "@apollo/client/errors";
import { nanoid } from "nanoid/non-secure";
import PubSub from "pubsub-js";
import { flatten, map } from "ramda";
import { createContext, type JSX, useContext } from "react";
import { extractExtensionsFromType, type FileDefinitionType } from "widgets/utils";
import { createStore, type StateCreator, type StoreApi, useStore } from "zustand";

import { query as Q } from "@/app-utils/graphql/query.ts";
import { PubSubActions } from "@/global/pubsub-actions.ts";
import type { Organism, Parent, PlaybookEvent, ProjectFile, Sample } from "@/graphql.ts";
import { fileSearchGridColumnDefs } from "@/modules/files/settings/column-defs.ts";
import { getContextualFileFilterOptions } from "@/modules/files/utils/getContextualFileFilterOptions.tsx";
import { getSortOrderExpressionFromTableColumns } from "@/redux/modules/table-data.ts";

import {
  SearchProjectFilesDocument,
  type SearchProjectFilesQuery,
  type SearchProjectFilesQueryVariables,
} from "../graphql/files-queries.gql.ts";

export enum ContextualFileFilter {
  ASSOCIATED_GENOMES = "ASSOCIATED_GENOMES",
  ASSOCIATED_EVENTS = "ASSOCIATED_EVENTS",
  ASSOCIATED_SAMPLES = "ASSOCIATED_SAMPLES",
  ASSOCIATED_PARENTS = "ASSOCIATED_PARENTS",
}

export interface ContextualFileFilterOption {
  key: ContextualFileFilter;
  label: JSX.Element;
  description: string;
  active: boolean;
}

export interface FileSuggestionsContextProps {
  organisms: Organism[] | undefined;
  parents: Parent[] | undefined;
  samples: Sample[] | undefined;
  events: PlaybookEvent[] | undefined;
}

interface FileSearchState {
  files: ProjectFile[] | null;
  totalResultsCount: number | null;
  loading: boolean;
  error?: ApolloError;
  selectedFiles: ProjectFile[];
  suggestionsContext?: Partial<FileSuggestionsContextProps>;
  activeContextualFilter: ContextualFileFilter | null;
  sortExpression: string;
}

interface FileSearchActions {
  searchProjectFiles: (
    searchTerms: string,
    fileDefinitionTypes?: FileDefinitionType[] | string[] | null,
    projectId?: number,
    genomeIds?: number[] | null,
    eventIds?: number[] | null,
    sampleIds?: number[] | null,
    parentIds?: number[] | null,
    sortOrder?: string | null,
    folderId?: number | null,
  ) => Promise<void>;
  setContext: (fileSuggestionsContext: FileSuggestionsContextProps) => void;
  setActiveContextualFilter: (filter?: ContextualFileFilter | null) => void;
  selectFiles: (files: ProjectFile[]) => void;
  clear: () => void;
  setSortExpression: (sortExpression: string) => void;
}

export type FileSearchStore = FileSearchState & FileSearchActions;

const initialState: FileSearchState = {
  files: null,
  totalResultsCount: null,
  loading: false,
  error: undefined,
  selectedFiles: [],
  suggestionsContext: undefined,
  activeContextualFilter: null,
  sortExpression: getSortOrderExpressionFromTableColumns(fileSearchGridColumnDefs),
};

let lastRequestId: string;

const searchProjectFilesQuery = Q<SearchProjectFilesQuery, SearchProjectFilesQueryVariables>(
  SearchProjectFilesDocument,
  { showAlert: false },
);

const fileSearchStoreStateCreator: StateCreator<FileSearchStore> = (set, get) => {
  PubSub.subscribe(PubSubActions.LOGOUT, () => {
    set(initialState);
  });

  return {
    ...initialState,
    searchProjectFiles: async (
      searchTerms,
      fileDefinitionTypes,
      projectId,
      genomeIds,
      eventIds,
      sampleIds,
      parentIds,
      sortOrder,
      folderId,
    ) => {
      const requestId = nanoid();
      lastRequestId = requestId;
      set({ loading: true, files: null, error: undefined });
      // Transform fileDef to extensions + add compression ext
      const fileExtensions = flatten(map(extractExtensionsFromType, fileDefinitionTypes ?? [])).filter(
        (value): value is string => typeof value === "string",
      );

      const { data, error } = await searchProjectFilesQuery({
        searchTerm: searchTerms,
        fileExtensions,
        projectId,
        genomeIds,
        eventIds,
        sampleIds,
        parentIds,
        sortOrder,
        folderId,
      });
      if (requestId !== lastRequestId) {
        // we ignore legacy requests
        return;
      }
      if (error) {
        set({ error: error as ApolloError, loading: false });
      } else {
        const files = data?.search.projectFilesContextSearch.projectFiles;
        const totalCount = data?.search.projectFilesContextSearch.totalCount;
        set({
          files,
          totalResultsCount: totalCount,
          loading: false,
        });
      }
    },
    selectFiles: (selectedFiles) => {
      set({ selectedFiles });
    },
    setContext: (fileSuggestionsContext) => {
      const contextualOptions = getContextualFileFilterOptions(fileSuggestionsContext);
      const defaultFilter = contextualOptions.filter((option) => option.active)?.[0].key;
      set({ suggestionsContext: fileSuggestionsContext, activeContextualFilter: defaultFilter });
    },
    setActiveContextualFilter: (filter) => {
      set({ activeContextualFilter: filter });
    },
    setSortExpression: (sortExpression) => {
      set({ sortExpression: sortExpression });
    },
    clear: () => {
      // keep the suggestionsContext since it is initialized outside the component
      const suggestionsContext = get().suggestionsContext;
      const activeContextualFilter = get().activeContextualFilter;
      set({ ...initialState, suggestionsContext, activeContextualFilter });
    },
  };
};

export const createFileSearchStore = () => createStore<FileSearchStore>(fileSearchStoreStateCreator);
export const FileSearchContext = createContext<StoreApi<FileSearchStore>>({} as StoreApi<FileSearchStore>);
export function useFileSearchStore<T>(selector: (state: FileSearchStore) => T): T {
  const store = useContext(FileSearchContext);

  if (!store) {
    throw new Error("Missing File Search Store Provider");
  }

  return useStore(store, selector);
}
